import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import {
  Plus,
  FileText,
  Trash2,
  Edit,
  Copy,
  MoreVertical,
  Loader2,
  Search,
  CheckCircle,
  Clock
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SavedOffers() {
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeMenu, setActiveMenu] = useState(null);

  useEffect(() => {
    loadOffers();
  }, []);

  const loadOffers = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/offers`);
      setOffers(response.data.offers || []);
    } catch (error) {
      console.error("Load offers error:", error);
      toast.error("Failed to load offers");
    } finally {
      setIsLoading(false);
    }
  };

  const deleteOffer = async (offerId) => {
    if (!window.confirm("Are you sure you want to delete this offer?")) return;
    
    try {
      await axios.delete(`${API}/offers/${offerId}`);
      toast.success("Offer deleted");
      loadOffers();
    } catch (error) {
      console.error("Delete offer error:", error);
      toast.error("Failed to delete offer");
    }
  };

  const duplicateOffer = async (offer) => {
    try {
      const response = await axios.post(`${API}/offers`, {
        clientName: `${offer.clientName} (Copy)`,
        industry: offer.industry,
        status: "draft",
        phases: offer.phases
      });
      
      if (response.data.offer_id) {
        toast.success("Offer duplicated");
        navigate(`/offers/build/${response.data.offer_id}`);
      }
    } catch (error) {
      console.error("Duplicate offer error:", error);
      toast.error("Failed to duplicate offer");
    }
  };

  const saveAsTemplate = async (offerId) => {
    try {
      await axios.post(`${API}/offers/${offerId}/save-as-template`);
      toast.success("Saved as template");
    } catch (error) {
      console.error("Save template error:", error);
      toast.error("Failed to save as template");
    }
  };

  const filteredOffers = offers.filter(offer =>
    offer.clientName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    offer.industry?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getCompletionStatus = (offer) => {
    const phases = offer.phases || {};
    const completedPhases = Object.values(phases).filter(p => p.aiOutput).length;
    return { completed: completedPhases, total: 6 };
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[#00d4aa]" />
      </div>
    );
  }

  return (
    <div className="animate-fadeIn">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Saved Offers</h1>
          <p className="text-white/60 text-sm mt-1">
            {offers.length} offer{offers.length !== 1 ? "s" : ""} saved
          </p>
        </div>
        
        <Button
          onClick={() => navigate("/offers/build")}
          className="bg-[#00d4aa] hover:bg-[#00e6bb] text-black font-semibold"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Offer
        </Button>
      </div>
      
      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search offers..."
          className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/40 focus:border-[#00d4aa] focus:outline-none"
        />
      </div>
      
      {/* Offers Grid */}
      {filteredOffers.length === 0 ? (
        <Card className="card">
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 text-white/20 mx-auto mb-4" />
            <h3 className="text-white font-medium mb-2">No offers found</h3>
            <p className="text-white/60 text-sm mb-6">
              {searchQuery ? "Try a different search term" : "Create your first offer to get started"}
            </p>
            {!searchQuery && (
              <Button
                onClick={() => navigate("/offers/build")}
                className="bg-[#00d4aa] hover:bg-[#00e6bb] text-black font-semibold"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Offer
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredOffers.map((offer) => {
            const status = getCompletionStatus(offer);
            const isComplete = status.completed === status.total;
            
            return (
              <Card
                key={offer.offer_id}
                className="card hover:border-white/20 transition-all cursor-pointer group"
                onClick={() => navigate(`/offers/build/${offer.offer_id}`)}
              >
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-white font-semibold text-lg line-clamp-1">
                        {offer.clientName || "Untitled Offer"}
                      </h3>
                      <p className="text-white/60 text-sm">{offer.industry || "No industry"}</p>
                    </div>
                    
                    <div className="relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveMenu(activeMenu === offer.offer_id ? null : offer.offer_id);
                        }}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                      >
                        <MoreVertical className="w-4 h-4 text-white/60" />
                      </button>
                      
                      {activeMenu === offer.offer_id && (
                        <div className="absolute right-0 top-full mt-1 w-48 bg-[#1a1a2e] border border-white/10 rounded-lg shadow-xl z-10">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/offers/build/${offer.offer_id}`);
                            }}
                            className="w-full px-4 py-2 text-left text-white/80 hover:bg-white/10 flex items-center gap-2"
                          >
                            <Edit className="w-4 h-4" />
                            Edit
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              duplicateOffer(offer);
                            }}
                            className="w-full px-4 py-2 text-left text-white/80 hover:bg-white/10 flex items-center gap-2"
                          >
                            <Copy className="w-4 h-4" />
                            Duplicate
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              saveAsTemplate(offer.offer_id);
                              setActiveMenu(null);
                            }}
                            className="w-full px-4 py-2 text-left text-white/80 hover:bg-white/10 flex items-center gap-2"
                          >
                            <FileText className="w-4 h-4" />
                            Save as Template
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteOffer(offer.offer_id);
                              setActiveMenu(null);
                            }}
                            className="w-full px-4 py-2 text-left text-red-400 hover:bg-white/10 flex items-center gap-2"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Progress */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between text-xs text-white/60 mb-2">
                      <span>Progress</span>
                      <span>{status.completed}/{status.total} phases</span>
                    </div>
                    <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#00d4aa] transition-all"
                        style={{ width: `${(status.completed / status.total) * 100}%` }}
                      />
                    </div>
                  </div>
                  
                  {/* Footer */}
                  <div className="flex items-center justify-between pt-4 border-t border-white/10">
                    <div className="flex items-center gap-2">
                      {isComplete ? (
                        <span className="flex items-center gap-1 text-xs text-[#00d4aa]">
                          <CheckCircle className="w-3 h-3" />
                          Complete
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-xs text-yellow-500">
                          <Clock className="w-3 h-3" />
                          Draft
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-white/40">
                      {new Date(offer.updatedAt).toLocaleDateString()}
                    </span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default SavedOffers;
